/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * InteractionSection — Accordion 5: search, filter, sort, pagination toggles.
 * Toggles use the current theme's primary colour when enabled.
 */

import React from 'react';
import { lookupFieldOptions, TEXT_TYPES, DATE_TYPES } from './relatedLookups';

/** Record detail page layouts offered to the public page. */
const DETAIL_LAYOUTS = [
  {
    value: 'card',
    label: 'Card',
    icon: 'bi-square',
    hint: 'Boxed white card — cover image on top, fields inside.',
  },
  {
    value: 'container',
    label: 'Container',
    icon: 'bi-layout-text-window-reverse',
    hint: 'Full-width hero with title overlay, fields in open panels.',
  },
];

/** Pill-style toggle switch — uses theme accent colour when on. */
function Toggle({ label, hint, checked, onChange, accentColor }) {
  return (
    <div className="d-flex align-items-center justify-content-between py-2 border-bottom">
      <div style={{ paddingRight: 12 }}>
        <div className="small fw-semibold">{label}</div>
        {hint && <div className="text-muted" style={{ fontSize: 11 }}>{hint}</div>}
      </div>
      <div
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        style={{
          flexShrink: 0,
          width: 42,
          height: 24,
          borderRadius: 12,
          background: checked ? accentColor : '#cbd5e1',
          position: 'relative',
          cursor: 'pointer',
          transition: 'background 200ms ease',
          boxShadow: checked ? `0 0 0 3px ${accentColor}28` : 'none',
        }}
      >
        <span style={{
          position: 'absolute',
          top: 3,
          left: checked ? 21 : 3,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.22)',
          transition: 'left 200ms ease',
        }} />
      </div>
    </div>
  );
}

/** A row of pill toggles choosing a subset of fields. */
function FieldChipPicker({ label, hint, fields, selected = [], onChange, accentColor }) {
  return (
    <div className="mt-3">
      <div className="small fw-semibold mb-2">{label}</div>
      {hint && <div className="text-muted mb-2" style={{ fontSize: 11 }}>{hint}</div>}
      <div className="d-flex flex-wrap gap-2">
        {fields.map(f => {
          const isSelected = selected.includes(f.name);
          return (
            <button
              key={f.name}
              type="button"
              onClick={() => onChange(isSelected
                ? selected.filter(n => n !== f.name)
                : [...selected, f.name])}
              style={{
                padding: '4px 14px',
                borderRadius: 999,
                fontSize: '0.8rem',
                fontWeight: 500,
                border: `1.5px solid ${isSelected ? accentColor : '#e2e8f0'}`,
                background: isSelected ? accentColor : '#fff',
                color: isSelected ? '#fff' : '#64748b',
                cursor: 'pointer',
                transition: 'all 150ms ease',
              }}
            >
              {f.display_name || f.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Previously: ({ config, updateConfig, fields = [], theme })
export default function InteractionSection({ config, updateConfig, fields = [], theme, relatedEdges = [] }) {
  const ic = config.interaction_config || {};
  const accentColor = theme?.primary || '#6366f1';

  function update(key, value) {
    updateConfig({ interaction_config: { ...ic, [key]: value } });
  }

  // const allFieldNames = config.visible_fields != null ? config.visible_fields : fields.map(f => f.name);
  // An EMPTY visible_fields means "none chosen", not "no fields": the public
  // list endpoint searches every public text field in that case, so the
  // pickers below must offer every field too. Previously only null counted as
  // unset, and an empty list hid Title/Description from Searchable Fields.
  const allFieldNames = config.visible_fields?.length ? config.visible_fields : fields.map(f => f.name);
  const enumBoolFields = fields.filter(f =>
    ['enum', 'boolean'].includes(f.data_type) &&
    allFieldNames.includes(f.name)
  );
  // The same text types the public list endpoint searches.
  const textFields = fields.filter(f =>
    ['string', 'text', 'varchar', 'char'].includes(f.data_type) &&
    allFieldNames.includes(f.name)
  );

  // Columns of the row each record points at (a pass's event), from Related
  // Data's "up" sources. Stored as `<alias>.<column>`; the server honours them
  // only while that source still publishes the column.
  const lookupOptions = lookupFieldOptions(config.related_config, relatedEdges);
  const lookupTextFields = lookupOptions
    .filter(o => TEXT_TYPES.includes(o.data_type))
    .map(o => ({ name: o.name, display_name: o.label }));

  // Default order: any own field, or any published lookup column.
  const orderOptions = [
    ...fields.map(f => ({ name: f.name, label: f.display_name || f.name, data_type: f.data_type })),
    ...lookupOptions,
  ];
  const defaultSort = ic.default_sort || '';
  const defaultSortType = (orderOptions.find(o => o.name === defaultSort) || {}).data_type;
  const canUpcoming = DATE_TYPES.includes(defaultSortType);
  const defaultDir = ic.default_sort_dir || 'desc';

  // Both keys in one write: two update() calls would each spread the same
  // stale `ic`, and the second would silently undo the first.
  function updateDefaultOrder(sort, dir) {
    const next = { ...ic, default_sort: sort, default_sort_dir: dir };
    if (!sort) {
      delete next.default_sort;
      delete next.default_sort_dir;
    }
    updateConfig({ interaction_config: next });
  }

  return (
    <div>
      <Toggle
        label="Search"
        hint="Search across text fields — narrow it with Searchable Fields"
        checked={!!ic.search_enabled}
        onChange={v => update('search_enabled', v)}
        accentColor={accentColor}
      />
      <Toggle
        label="Filter Chips"
        hint="Dropdown filter chips for enum/boolean fields"
        checked={!!ic.filter_enabled}
        onChange={v => update('filter_enabled', v)}
        accentColor={accentColor}
      />
      <Toggle
        label="Sort Columns"
        hint="Sort toolbar in all views; column headers in table view"
        checked={!!ic.sort_enabled}
        onChange={v => update('sort_enabled', v)}
        accentColor={accentColor}
      />
      <Toggle
        label="Record Detail Page"
        hint="Allow visitors to click through to individual record pages"
        checked={ic.detail_enabled !== false}
        onChange={v => update('detail_enabled', v)}
        accentColor={accentColor}
      />
      <Toggle
        label="Only show each visitor their own rows"
        hint="Signed-in visitors see only records they created; anyone signed out is asked to sign in. Applies to the list AND the record page. Use for registrations, orders and submissions."
        checked={!!ic.owner_scoped}
        onChange={v => update('owner_scoped', v)}
        accentColor={accentColor}
      />
      {!!ic.owner_scoped && (
        <div
          className="small"
          style={{
            margin: '-4px 0 14px', padding: '8px 10px', borderRadius: 6,
            background: '#FFF7E6', border: '1px solid #F0D9A8', color: '#8A5A00',
            lineHeight: 1.45,
          }}
        >
          Responses stop being cached publicly and are answered from the visitor's
          session. Turn this on <strong>before</strong> making the table public — a
          shared cache can otherwise still serve the old everyone-list for a few minutes.
        </div>
      )}

      {/* QR column — only meaningful on an owner-scoped view, because every row
          has to already belong to the caller before a scannable code is drawn. */}
      {!!ic.owner_scoped && (
        <div className="mb-3">
          <label className="form-label small fw-semibold mb-1" style={{ color: '#475569' }}>
            Show a QR code from
          </label>
          <select
            className="form-select form-select-sm"
            value={ic.qr_column || ''}
            onChange={e => update('qr_column', e.target.value)}
          >
            <option value="">— No QR code —</option>
            {fields.map(f => (
              <option key={f.name} value={f.name}>{f.display_name || f.name}</option>
            ))}
          </select>
          <div className="form-text" style={{ fontSize: 11 }}>
            The column holding the scannable token. Only the picture is sent —
            the value itself never reaches the browser, so keep this column
            hidden under Fields.
          </div>
        </div>
      )}

      {/* Button label — only relevant when detail page is enabled */}
      {ic.detail_enabled !== false && (
        <div
          className="mt-1 mb-2 ms-1 ps-3 py-2"
          style={{ borderLeft: `2px solid ${accentColor}40` }}
        >
          <label className="form-label small fw-semibold mb-1" style={{ color: '#475569' }}>
            Button Label
          </label>
          <div className="d-flex align-items-center gap-2">
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ maxWidth: 160 }}
              placeholder="View"
              value={ic.detail_button_label || ''}
              onChange={e => update('detail_button_label', e.target.value)}
              maxLength={30}
            />
            {ic.detail_button_label ? (
              <span
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: '0.72rem', fontWeight: 700,
                  color: '#fff', background: accentColor,
                  padding: '3px 10px', borderRadius: 999,
                  whiteSpace: 'nowrap',
                }}
              >
                {ic.detail_button_label} →
              </span>
            ) : (
              <span style={{ fontSize: '0.72rem', color: accentColor, letterSpacing: 1 }}>→</span>
            )}
          </div>
          <div className="form-text mt-1" style={{ fontSize: '0.7rem' }}>
            Leave empty to show an arrow icon only.
          </div>
        </div>
      )}

      {/* Detail Page Layout — card (boxed) vs container (full-width hero) */}
      <div className="mt-3">
        <div className="small fw-semibold mb-2">Detail Page Layout</div>
        <div className="d-flex gap-2">
          {DETAIL_LAYOUTS.map(opt => {
            const isActive = (ic.detail_layout || 'card') === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => update('detail_layout', opt.value)}
                style={{
                  flex: 1,
                  textAlign: 'left',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: `1.5px solid ${isActive ? accentColor : '#e2e8f0'}`,
                  background: isActive ? `${accentColor}0f` : '#fff',
                  cursor: 'pointer',
                  transition: 'all 150ms ease',
                }}
              >
                <div className="d-flex align-items-center gap-2 mb-1">
                  <i
                    className={`bi ${opt.icon}`}
                    style={{ fontSize: '0.95rem', color: isActive ? accentColor : '#94a3b8' }}
                  />
                  <span style={{
                    fontSize: '0.8rem',
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? accentColor : '#475569',
                  }}>
                    {opt.label}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.35 }}>
                  {opt.hint}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail Page Columns — applies to both layouts */}
      <div className="mt-3 mb-1">
        <div className="small fw-semibold mb-2">Detail Page Columns</div>
        <div className="d-flex gap-2">
          {[
            { value: 1, label: '1 Column' },
            { value: 2, label: '2 Columns' },
            { value: 3, label: '3 Columns' },
          ].map(opt => {
            const isActive = Number(ic.detail_columns || 2) === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => update('detail_columns', opt.value)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 7,
                  fontSize: '0.78rem',
                  fontWeight: isActive ? 700 : 400,
                  border: `1.5px solid ${isActive ? accentColor : '#e2e8f0'}`,
                  background: isActive ? `${accentColor}15` : '#fff',
                  color: isActive ? accentColor : '#64748b',
                  cursor: 'pointer',
                  transition: 'all 150ms ease',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Previously own text fields only:
      {ic.search_enabled && textFields.length > 0 && (
        <FieldChipPicker
          label="Searchable Fields"
          hint="None selected searches every text field."
          fields={textFields}
          selected={ic.searchable_fields}
          onChange={v => update('searchable_fields', v)}
          accentColor={accentColor}
        />
      )} */}
      {ic.search_enabled && (textFields.length > 0 || lookupTextFields.length > 0) && (
        <FieldChipPicker
          label="Searchable Fields"
          hint={lookupTextFields.length
            ? 'None selected searches every text field of this table. "›" fields come from a table this one points to (Related Data).'
            : 'None selected searches every text field.'}
          fields={[...textFields, ...lookupTextFields]}
          selected={ic.searchable_fields}
          onChange={v => update('searchable_fields', v)}
          accentColor={accentColor}
        />
      )}

      {ic.filter_enabled && enumBoolFields.length > 0 && (
        <FieldChipPicker
          label="Filterable Fields"
          fields={enumBoolFields}
          selected={ic.filterable_fields}
          onChange={v => update('filterable_fields', v)}
          accentColor={accentColor}
        />
      )}

      {/* Default order — used whenever the visitor has not picked a sort. */}
      <div className="mt-3">
        <div className="small fw-semibold mb-2">Default Order</div>
        <div className="d-flex gap-2 flex-wrap">
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 240 }}
            aria-label="Default order field"
            value={defaultSort}
            onChange={e => {
              const name = e.target.value;
              const type = (orderOptions.find(o => o.name === name) || {}).data_type;
              // "Upcoming first" only means something on a date; anything else
              // keeps asc/desc, starting from the stored direction.
              const dir = defaultDir === 'upcoming' && !DATE_TYPES.includes(type) ? 'asc' : defaultDir;
              updateDefaultOrder(name, dir);
            }}
          >
            <option value="">— No default (as stored) —</option>
            {orderOptions.map(o => (
              <option key={o.name} value={o.name}>{o.label}</option>
            ))}
          </select>
          {defaultSort && (
            <select
              className="form-select form-select-sm"
              style={{ maxWidth: 260 }}
              aria-label="Default order direction"
              value={defaultDir === 'upcoming' && !canUpcoming ? 'asc' : defaultDir}
              onChange={e => updateDefaultOrder(defaultSort, e.target.value)}
            >
              <option value="asc">Ascending (A→Z, oldest first)</option>
              <option value="desc">Descending (Z→A, newest first)</option>
              {canUpcoming && (
                <option value="upcoming">Upcoming first, then most recent past</option>
              )}
            </select>
          )}
        </div>
        <div className="form-text" style={{ fontSize: 11 }}>
          Applies until a visitor picks a sort. "›" fields come from Related Data.
        </div>
      </div>

      {/* Pagination style */}
      <div className="mt-3">
        <div className="small fw-semibold mb-2">Pagination Style</div>
        <div className="d-flex gap-3">
          {[
            { value: 'pages',     label: 'Pages (Prev / Next)' },
            { value: 'load_more', label: 'Load More button' },
          ].map(opt => {
            const isActive = (ic.pagination_style || 'pages') === opt.value;
            return (
              <div
                key={opt.value}
                onClick={() => update('pagination_style', opt.value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  padding: '6px 14px',
                  borderRadius: 8,
                  border: `1.5px solid ${isActive ? accentColor : '#e2e8f0'}`,
                  background: isActive ? accentColor + '12' : '#fff',
                  transition: 'all 150ms ease',
                }}
              >
                {/* Custom radio dot */}
                <span style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: `2px solid ${isActive ? accentColor : '#cbd5e1'}`,
                  background: isActive ? accentColor : '#fff',
                  flexShrink: 0,
                  transition: 'all 150ms ease',
                }} />
                <span className="small" style={{ color: isActive ? accentColor : '#64748b', fontWeight: isActive ? 600 : 400 }}>
                  {opt.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Page size */}
      <div className="mt-3">
        <label className="form-label small fw-semibold">Records Per Page</label>
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: 160 }}
          // value={ic.page_size || 20}
          // 12 is what the server uses when no page size is saved
          // (public_view_helper.build_default_interaction_config), so an unset
          // view shows the size visitors actually get.
          value={ic.page_size || 15}
          onChange={e => update('page_size', Number(e.target.value))}
        >
          {/* {[10, 20, 50, 100].map(n => ( */}
          {[10, 15, 20, 50, 100].map(n => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
